import React, { ReactElement } from 'react';
import Layout from 'src/elements/layout/layout';
import CoreValues from 'src/organisms/coreValues/coreValues';
import CareersList from 'src/organisms/careers/careers.list';
import OurTeam from 'src/organisms/ourTeam/ourTeam';
import WorkCulture from 'src/organisms/workCulture/workCulture';
import Benefits from 'src/organisms/benefits/benefits';
import InterviewProcess from 'src/organisms/interviewProcess/interviewProcess';
import { graphql, PageProps } from 'gatsby';
import {
  BenefitContentfulModel,
  BenefitsSectionContentfulModel,
  CareersBannerSectionContentfulModel,
  CareersImageContentfulModel,
  CoreValueContentfulModel,
  CoreValuesSectionContentfulModel,
  InterviewProcessItemContentfulModel,
  InterviewProcessSectionContentfulModel,
  OpenPositionsSectionContentfulModel,
  OurMissionSectionContentfulModel,
  TestimonySectionContentfulModel,
  WorkCultureSectionContentfulModel,
} from 'src/api/models/Careers';
import { HeadingContentfulModel } from 'src/api/models/Heading';
import { ShortParagraphContentModel } from 'src/api/models/ShortParagraph';
import { ImageContentfulModel } from 'src/api/models/Image';
import { ButtonContentfulModel } from 'src/api/models/Button';
import Testimony from 'src/molecules/testimony/testimony';
import PromotionModal from 'src/organisms/promotion/PromotionModal';

interface CareersPageProps extends PageProps {
  data: {
    contentfulPage: {
      title: string;
      seoTitle: string;
      seoDescription?: string;
      displayPromotionModal?: boolean;
      seoImage?: {
        file?: {
          url: string;
        }
      },
      contentfulchildren: (
        CareersBannerSectionContentfulModel |
        OpenPositionsSectionContentfulModel |
        OurMissionSectionContentfulModel |
        CoreValuesSectionContentfulModel |
        WorkCultureSectionContentfulModel |
        TestimonySectionContentfulModel |
        BenefitsSectionContentfulModel |
        InterviewProcessSectionContentfulModel
      )[]
    }
  };
}

const CareersPage = ({ location, data }: CareersPageProps): ReactElement => {
  const pageContent = data.contentfulPage;

  const helmetData = {
    title: pageContent.seoTitle,
    description: pageContent.seoDescription,
    previewImageURL: pageContent.seoImage?.file?.url
  }

  const displayModal = pageContent.displayPromotionModal;

  const ourTeamSectionData = (pageContent.contentfulchildren as CareersBannerSectionContentfulModel[])
    .find(data => data.label.includes('Our Team Banner Container')).contentfulchildren;
  const ourTeamHeader = ourTeamSectionData.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const ourTeamSubtitle = ourTeamSectionData.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel;
  const ourTeamButton = ourTeamSectionData.find(data => data.internal.type === 'ContentfulButton') as ButtonContentfulModel;
  const ourTeamImages = ourTeamSectionData.find(data => data.internal.type === 'ContentfulImage') as ImageContentfulModel;

  const careersListSection = (pageContent.contentfulchildren as OpenPositionsSectionContentfulModel[])
    .find(data => data.label.includes('Open Positions Container')).contentfulchildren;
  const careersListTitle = careersListSection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;

  const ourMissionSection = (pageContent.contentfulchildren as OurMissionSectionContentfulModel[])
    .find(data => data.label.includes('Our Mission Container')).contentfulchildren;
  const ourMissionTitle = ourMissionSection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const ourMissionSubtitle = ourMissionSection.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel;

  const coreValuesSection = (pageContent.contentfulchildren as CoreValuesSectionContentfulModel[])
    .find(data => data.label.includes('Core Values Container')).contentfulchildren;
  const coreValuesTitle = coreValuesSection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const coreValues = (coreValuesSection.filter(data => data.internal.type === 'ContentfulSection') as CoreValueContentfulModel[])
    .map(coreValue => {
      const name = (coreValue.contentfulchildren.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel)?.text;
      const image = (coreValue.contentfulchildren.find(data => data.internal.type === 'ContentfulImage') as CareersImageContentfulModel)?.careersImage.coreValue;

      return {
        name,
        image
      }
    });

  const workCultureSection = (pageContent.contentfulchildren as WorkCultureSectionContentfulModel[])
    .find(data => data.label.includes('Work Culture Container')).contentfulchildren;
  const workCultureTitle = workCultureSection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const workCultureText = workCultureSection.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel;
  const workCultureImage = workCultureSection.find(data => data.internal.type === 'ContentfulImage') as ImageContentfulModel;

  const firstTestimonySection = (pageContent.contentfulchildren as TestimonySectionContentfulModel[])
    .find(data => data.label.includes('First Testimony Container')).contentfulchildren;
  const firstTestimonyImage = firstTestimonySection.find(data => data.internal.type === 'ContentfulImage') as ImageContentfulModel;
  const firstTestimonyText = firstTestimonySection.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel;
  const firstTestimonyPersonName = firstTestimonySection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;

  const benefitsSection = (pageContent.contentfulchildren as BenefitsSectionContentfulModel[])
    .find(data => data.label.includes('Culture and Benefits Container')).contentfulchildren;
  const benefitsTitle = benefitsSection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const benefitsSubtitle = benefitsSection.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel;
  const benefitsItems = (benefitsSection.filter(data => data.internal.type === 'ContentfulSection') as BenefitContentfulModel[])
    .map(item => {
      const name = (item.contentfulchildren.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel).text;
      const description = (item.contentfulchildren.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel).text;
      const image = (item.contentfulchildren.find(data => data.internal.type === 'ContentfulImage') as CareersImageContentfulModel).careersImage.benefit;

      return {
        name,
        description,
        image
      }
    })

  const secondTestimonySection = (pageContent.contentfulchildren as TestimonySectionContentfulModel[])
    .find(data => data.label.includes('Second Testimony Container')).contentfulchildren;
  const secondTestimonyImage = secondTestimonySection.find(data => data.internal.type === 'ContentfulImage') as ImageContentfulModel;
  const secondTestimonyText = secondTestimonySection.find(data => data.internal.type === 'ContentfulShortParagraph') as ShortParagraphContentModel;
  const secondTestimonyPersonName = secondTestimonySection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;

  const interviewProcessSection = (pageContent.contentfulchildren as InterviewProcessSectionContentfulModel[])
    .find(data => data.label.includes('Interview Process Container')).contentfulchildren;
  const interviewProcessTitle = interviewProcessSection.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel;
  const interviewProcessButton = interviewProcessSection.find(data => data.internal.type === 'ContentfulButton') as ButtonContentfulModel;
  const interviewProcessSteps = (interviewProcessSection.filter(data => data.internal.type === 'ContentfulSection') as InterviewProcessItemContentfulModel[])
    .map(step => {
      const name = (step.contentfulchildren.find(data => data.internal.type === 'ContentfulHeading') as HeadingContentfulModel).text;
      const image = (step.contentfulchildren.find(data => data.internal.type === 'ContentfulImage') as CareersImageContentfulModel).careersImage.interviewProcess;

      return {
        name,
        image
      }
    })

  return (
    <Layout helmet={helmetData} currentPageId="5cmqHuUeGGmMGbaSEBvQvi" location={location}>
      <OurTeam
        variant="careers"
        title={ourTeamHeader.text}
        subtitle={ourTeamSubtitle.text}
        showCulturalFit={false}
        buttonText={ourTeamButton.text}
        ourTeamImage={ourTeamImages?.image?.fullWidthImageData}
        ourTeamMobileImage={ourTeamImages?.mobileImage?.fullWidthImageData}
      />
      <CareersList title={careersListTitle.text} />
      <CoreValues
        sectionTitle={ourMissionTitle.text}
        sectionSubtitle={ourMissionSubtitle.text}
        coreValuesTitle={coreValuesTitle.text}
        coreValuesList={coreValues}
      />
      <WorkCulture
        title={workCultureTitle.text}
        substitle={workCultureText.text}
        image={workCultureImage.image.fullWidthImageData}
        mobileImage={workCultureImage.mobileImage.fullWidthImageData}
      />
      <Testimony
        image={firstTestimonyImage.image.fullWidthImageData}
        text={firstTestimonyText.text}
        personName={firstTestimonyPersonName.text}
      />
      <Benefits
        title={benefitsTitle.text}
        subtitle={benefitsSubtitle.text}
        items={benefitsItems}
      />
      <Testimony
        image={secondTestimonyImage.image.fullWidthImageData}
        text={secondTestimonyText.text}
        personName={secondTestimonyPersonName.text}
      />
      <InterviewProcess
        title={interviewProcessTitle.text}
        steps={interviewProcessSteps}
        buttonText={interviewProcessButton.text}
      />
      <PromotionModal displayModal={displayModal}/>
    </Layout>
  )
}

export const pageQuery = graphql`
  query CareersPageQuery {
    contentfulPage(contentful_id: {eq: "5cmqHuUeGGmMGbaSEBvQvi"}) {
      title
      seoTitle
      seoDescription
      seoImage {
        file {
          url
        }
      }
      displayPromotionModal
      contentfulchildren {
        id
        label
        contentfulchildren {
          ...HeadingContent
          ...ShortParagraphContent
          ...ImageContent
          ...ButtonContent
          ...CustomizableSection
        }
      }
    }
  }
`;

export default CareersPage;
